<template>
    <div>
        <app-bar :title="t('collections_title')">
            <template #actions>
                <div class="px-10">
                    <v-btn
                        :to="{ name: 'workout-collections.create' }"
                        class="sm sm-h"
                        color="orange"
                    >{{ t('collection_title_add') }}</v-btn>
                </div>
            </template>
        </app-bar>

        <v-layout>
            <v-progress-linear
                v-if="overlay"
                absolute
                indeterminate
            ></v-progress-linear>

            <card-collection
                class="px-4 my-7"
                :meta="meta"
                :busy="overlay"
                @updated="updated"
            >
                <template #filters>
                    <v-toolbar
                        elevation="0"
                        color="transparent"
                        class="mt-7"
                        height="56"
                    >
                        <v-text-field
                            v-model="queries.search"
                            @click:clear="clear"
                            class="input-search md mr-2"
                            clearable
                            outlined
                            dense
                            hide-details
                            prepend-inner-icon="mdi-magnify"
                            :label="t('general_label_search')"
                        ></v-text-field>

                        <v-spacer></v-spacer>

                        <v-select
                            v-model="queries.level"
                            :items="[ { text: '-', value: '' } ].concat(levels())"
                            class="md mr-2"
                            outlined
                            dense
                            append-icon="mdi-chevron-down"
                            hide-details
                            :label="t('collection_label_level')"
                        ></v-select>

                        <v-spacer></v-spacer>

                        <v-checkbox
                            v-model="queries.only_suspended"
                            :label="t('general_label_show_suspended')"
                            hide-details
                            class="ma-0 mr-5 customized-deep-primary-color"
                        />
                        <v-checkbox
                            v-model="queries.archived"
                            :label="t('collection_label_show_archived')"
                            hide-details
                            class="ma-0 mr-5 customized-deep-primary-color"
                        />

                        <v-btn
                            @click="search"
                            class="sm mx-5"
                            color="primary"
                        >{{ t('general_action_search') }}</v-btn>

                        <v-spacer></v-spacer>

                        <v-btn
                            v-ripple="false"
                            plain
                            color="orange darken-4"
                            @click="reset"
                        >{{ t('general_action_reset_filters') }}</v-btn>
                    </v-toolbar>
                </template>

                <template #data>
                    <thead>
                    <tr>
                        <th class="touched">
                            <sort-field
                                v-model="queries.sort"
                                field="id"
                                @touched="onSort"
                            >{{ t('collection_label_id') }}
                            </sort-field>
                        </th>
                        <th>
                            <sort-field
                                v-model="queries.sort"
                                field="name"
                                @touched="onSort"
                            >{{ t('collection_label_name') }}
                            </sort-field>
                        </th>
                        <th>{{ t('collection_label_level') }}</th>
                        <th>{{ t('collection_label_workouts') }}</th>
                        <th>
                            <sort-field
                                v-model="queries.sort"
                                field="created_at"
                                @touched="onSort"
                            >{{ t('general_label_creation_date') }}
                            </sort-field>
                        </th>
                        <th>
                            <sort-field
                                v-model="queries.sort"
                                field="sort_order"
                                @touched="onSort"
                            >{{ t('collection_label_sort_order') }}
                            </sort-field>
                        </th>
                        <th class="text-center">{{ t('general_label_action') }}</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-if="collection.data.length === 0">
                        <td class="text-center" colspan="7">{{ t('general_text_no_data') }}</td>
                    </tr>
                    <template v-else v-for="item in collection.data">
                        <tr>
                            <td>
                                <a
                                    :href="`/workout-collections/${item.id}/view`"
                                    @click.prevent="touch(item)"
                                    :class="{ 'orange--text': ! item.enabled }"
                                >{{ item.id }}</a>
                            </td>
                            <td>
                                <a
                                    :class="levelColor(item.level) + '--text'"
                                    :href="`/workout-collections/${item.id}/view`"
                                    @click.prevent="touch(item)"
                                >{{ item.name }}</a>
                            </td>
                            <td>{{ levelTitle(item.level) }}</td>
                            <td>{{ item.workoutCount }}</td>
                            <td>{{ item.createdAt.format('DD/MM/YYYY') }}</td>
                            <td>{{ item.sortOrder }}</td>
                            <td class="text-center">
                                <v-menu
                                    offset-y
                                    left
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            icon
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            <v-icon>mdi-dots-vertical</v-icon>
                                        </v-btn>
                                    </template>
                                    <v-list dense>
                                        <v-list-item
                                            @click.prevent="edit(item)"
                                        >
                                            <v-list-item-title
                                                class="customized-deep-primary--text"
                                            >{{ t('general_action_edit') }}
                                            </v-list-item-title>
                                        </v-list-item>
                                        <v-list-item
                                            @click.prevent="suspend(item)"
                                        >
                                            <v-list-item-title
                                                v-if="item.enabled"
                                                class="customized-deep-primary--text"
                                            >{{ t('general_action_suspend') }}
                                            </v-list-item-title>
                                            <v-list-item-title
                                                v-else
                                                class="orange--text"
                                            >{{ t('general_action_unsuspend') }}
                                            </v-list-item-title>
                                        </v-list-item>
                                        <confirm-modal
                                            :title="t('general_title_confirm_action')"
                                            @agree="del(item)"
                                        >
                                            <template #content>
                                                <div class="text-center">{{ t('collection_warning_delete') }}</div>
                                            </template>
                                            <template #default="{ on, attrs }">
                                                <v-list-item
                                                    href
                                                    v-on="on"
                                                    v-bind="attrs"
                                                >
                                                    <v-list-item-title
                                                        class="customized-deep-primary--text"
                                                    >{{ t('general_action_delete') }}</v-list-item-title>
                                                </v-list-item>
                                            </template>
                                        </confirm-modal>
                                    </v-list>
                                </v-menu>
                            </td>
                        </tr>
                    </template>
                    </tbody>
                </template>
            </card-collection>
        </v-layout>
    </div>
</template>

<script lang="ts">
    import { computed, defineComponent, onMounted, reactive, toRefs, watch } from '@vue/composition-api'
    import { useI18n } from 'vue-i18n-composable'
    import { useStore } from '@/store'
    import { useRouter } from '@/composition-api'
    import { useEnums } from '@/core/defaults'
    import { WorkoutCollectionList } from '@/store/modules/workout-collection-list'
    import { WorkoutCollectionModel } from '@/core/models/workout-collection'
    import CardCollection from '@/components/CardCollection.vue'
    import ConfirmModal from '@/components/ConfirmModal.vue'
    import SortField from '@/components/SortField.vue'

    export default defineComponent({

        components: {
            CardCollection,
            ConfirmModal,
            SortField,
        },

        setup(props) {
            const state = reactive({
                overlay: true,
                meta: undefined,
            })

            const queries = reactive({
                search: undefined,
                page: 1,
                per_page: 20,
                sort: '-id',
                level: '',
                only_suspended: undefined,
                archived: undefined,
            })

            const store = useStore()
            const router = useRouter()

            const collection = computed(() => <WorkoutCollectionList>store.getters['workoutCollectionList/get'])

            const updated = (updatedQueries: any) => {
                queries.page = updatedQueries.page
                capture()
            }

            const capture = () => {
                state.overlay = true
                store.dispatch('workoutCollectionList/get', queries).then((data) => {
                    state.meta = data.meta
                    state.overlay = false
                })
            }

            const reset = () => {
                queries.search = undefined
                queries.level = ''
                queries.only_suspended = undefined
                queries.archived = undefined
                capture()
            }

            const touch = (item: WorkoutCollectionModel) => {
                router.push({ name: 'workout-collections.view', params: <any>{ id: item.id } })
            }

            const edit = (item: WorkoutCollectionModel) => {
                router.push({ name: 'workout-collections.show', params: <any>{ id: item.id } })
            }

            const suspend = (item: WorkoutCollectionModel) => {
                state.overlay = true
                store.dispatch('workoutCollectionItem/update', {
                    id: item.id,
                    payload: { enabled: ! item.enabled }
                }).then(() => {
                    capture()
                })
            }

            const del = (item: WorkoutCollectionModel) => {
                state.overlay = true
                store.dispatch('workoutCollectionItem/delete', { id: item.id }).then(() => {
                    capture()
                })
            }

            const search = () => {
                queries.page = 1
                capture()
            }

            const clear = () => {
                queries.search = undefined
                capture()
            }

            const onSort = (value: any) => {
                if (value === undefined) {
                    value = '-id'
                }
                queries.sort = value
            }

            watch(() => [
                queries.sort,
                queries.level,
                queries.only_suspended,
                queries.archived,
            ], () => {
                capture()
            })

            onMounted(() => {
                collection.value.data = []
                capture()
            })

            return {
                queries,
                collection: collection.value,
                updated,
                touch,
                edit,
                suspend,
                del,
                onSort,
                search,
                clear,
                reset,
                ...useI18n(),
                ...useEnums(),
                ...toRefs(state)
            }
        }
    })
</script>
